import { APP_PARAMS } from '@/config/site-pages';
import { CAPTION_LANGUAGES, type CaptionLanguage, type CaptionTask } from '@/core/captions/types';
import { DEFAULT_OUTPUT, kindOf, OUTPUT_FORMATS, type OutputFormatId } from '@/core/project/output-format';
import { placementById, type Placement } from '@/core/project/placement';
import { parseSizeLimit } from '@/core/project/size-target';
import { BUILT_IN_PRESETS, type Preset } from '@/core/project/preset';

/**
 * Everything a link to the app can ask for (the content pages' buttons write
 * these with `appLink`). Read and checked here only, so each new parameter is
 * parsed and tested in one place. Anything unknown or malformed reads as "not
 * asked for": a link can never put the app in a state it couldn't reach itself.
 */
export interface AppParams {
  /** A built-in recipe to start on (`?for=privacy`). */
  readonly recipe: Preset | null;
  /** Quick convert: dropped videos go straight to saving in this format (`?to=mp4`; `?target` alone means MP4). */
  readonly output: OutputFormatId | null;
  /** Keep the video under this many MB (`?target=20mb`); only with a video format. */
  readonly sizeLimit: number | null;
  /** Open the metadata inspector (`?inspect=1`); it takes the place of converting. */
  readonly inspect: boolean;
  /** Fit the video for where it will be posted (`?fit=tiktok`): opens the editor with its shape, length check and largest file. */
  readonly placement: Placement | null;
  /** Captions in a language, or translated into English (`?lang=tr&task=translate`): the editor opens on them, ready to write. */
  readonly captions: CaptionLink | null;
}

export interface CaptionLink {
  /** The language spoken; null leaves it to detection or the person's choice. */
  readonly language: Exclude<CaptionLanguage, 'auto'> | null;
  /** `translate` when English was asked for, else null (as spoken). */
  readonly task: Extract<CaptionTask, 'translate'> | null;
}

/** Longer values are never real; reading them would only cost time. */
const MAX_VALUE_LENGTH = 64;

/** One parameter's value, trimmed and lowercased; null when missing, empty or implausibly long. */
function value(params: URLSearchParams, name: string): string | null {
  const raw = params.get(name)?.trim().toLowerCase();
  return raw && raw.length <= MAX_VALUE_LENGTH ? raw : null;
}

export const builtInRecipe = (id: string | null): Preset | undefined => (id ? BUILT_IN_PRESETS.find((preset) => preset.builtIn === id) : undefined);

/** An output format id; also forgives the common "jpeg" spelling. */
function outputFormat(id: string | null): OutputFormatId | null {
  const normal = id === 'jpeg' ? 'jpg' : id;
  return normal && Object.hasOwn(OUTPUT_FORMATS, normal) ? (normal as OutputFormatId) : null;
}

/** "20mb", "20 MB", "9.5mb" or "20": megabytes (see `parseSizeLimit` for the range). */
const sizeLimit = (text: string | null): number | null => (text ? parseSizeLimit(text.replace(/\s*mb$/, '')) : null);

/** A spoken language the captions offer (never `auto`: that is what no link means). */
function captionLanguage(code: string | null): CaptionLink['language'] {
  return code && code !== 'auto' && (CAPTION_LANGUAGES as readonly string[]).includes(code) ? (code as CaptionLink['language']) : null;
}

/** What a link asks of the captions, or null when nothing. */
function captionLink(params: URLSearchParams): CaptionLink | null {
  const language = captionLanguage(value(params, APP_PARAMS.captionLanguage));
  const task = value(params, APP_PARAMS.captionTask) === 'translate' ? 'translate' : null;
  return language || task ? { language, task } : null;
}

export function parseAppParams(search: string): AppParams {
  const params = new URLSearchParams(search);
  // Only the values a link writes; anything else is "not asked for".
  if (['1', 'true'].includes(value(params, APP_PARAMS.inspect) ?? '')) return { recipe: null, output: null, sizeLimit: null, inspect: true, placement: null, captions: null };
  const recipe = builtInRecipe(value(params, APP_PARAMS.recipe)) ?? null;
  const captions = captionLink(params);
  // Fitting is editing (the frame wants a look, the length may want a trim), so it takes the place of converting; the placement brings its own size.
  const placement = placementById(value(params, APP_PARAMS.placement));
  if (placement) return { recipe, output: null, sizeLimit: null, inspect: false, placement, captions };
  const limit = sizeLimit(value(params, APP_PARAMS.sizeLimit));
  const asked = outputFormat(value(params, APP_PARAMS.output));
  // A size only means something for a video; asked alone, it means "compress this video".
  const output = asked ?? (limit ? DEFAULT_OUTPUT : null);
  return {
    recipe,
    output,
    sizeLimit: output && kindOf(output) === 'video' ? limit : null,
    inspect: false,
    placement: null,
    // Converting writes no captions.
    captions: output ? null : captions,
  };
}
